import {Tr} from "@/i18n/i18n";
import Container = Phaser.GameObjects.Container;
import {Keys} from "@/constants/key";
import {ShopArtifact} from "@/store/shop/types";
import TextUtils from "@/utils/text-utils";
import {Fonts} from "@/constants/font";
import ArtifactUtils from "@/utils/artifact-utils";
import {SquareButton} from "@/scenes.ui/square-button";
import {EffectType} from "@/store/artifact/types";
import SoundUtils from "@/utils/sound-utils";
import SpriteUtils from "@/utils/sprite-utils";
import CU from "../utils/c-u";
import SkillUtils from "@/utils/skill-utils";
import OnceCaller from "@/utils/once-caller";
import GameObject = Phaser.GameObjects.GameObject;

export class ArtifactShopPanel extends Container {

    public static HEIGHT = 164;

    constructor(scene: Phaser.Scene, x: number, y: number, sa: ShopArtifact, manualCallback: (artifactId:string) => void, callback: () => void) {
        super(scene);

        this.scene = scene;
        this.x = x;
        this.y = y;
        let multiple= sa.artifacts.length > 1;
        this.width = CU.CANVAS_WIDTH;
        this.height = multiple? sa.artifacts.length * 130 + 50: ArtifactShopPanel.HEIGHT;

        const clickSound = SoundUtils.addSound(scene, Keys.SOUND_SELECT);

        const back = SpriteUtils.addImage(this.scene, 0, 0, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_PANEL_BACK).setOrigin(0, 0);
        SpriteUtils.setDispSize(back, CU.CANVAS_WIDTH, this.height * CU.S);

        let gameObjects: GameObject[] = [];
        if (multiple){
            let ribbon = SpriteUtils.addImage(this.scene!, CU.CANVAS_WIDTH/2 / CU.S, -57, Keys.RIBBON_FOR_BUNCH, undefined, 7, 2).setOrigin(0.5, 0)
            ribbon.alpha = 0.1;
            let titleText = TextUtils.addTextAdvanced(scene, sa.bunchName.toUpperCase(), CU.CANVAS_WIDTH/2 / CU.S, 20,
                '#eec761', 18, 'normal', CU.CANVAS_WIDTH/CU.S, 'left', 2, Fonts.BENGALY_NORMAL).setOrigin(0.5, 0);
            titleText.alpha = 0.9;
            gameObjects.push(ribbon, titleText);
        }

        let dy = multiple? 30: 0;
        sa.artifacts.forEach((a, i) => {
            const image = SpriteUtils.addImage(this.scene, 60, ArtifactShopPanel.HEIGHT / 2 + dy, a.atlasKey, a.imageKey, 0.9);

            const imageLiner = SpriteUtils.addImage(this.scene, 60, ArtifactShopPanel.HEIGHT / 2 + dy, Keys.ATLAS_UI, Keys.UI_BADGE_LINER);

            const titleLight = SpriteUtils.addImage(this.scene, 129 - a.name.length * 5, 15 + dy, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_LIGHT)
                .setOrigin(0, 0);
            SpriteUtils.setDispSize(titleLight, 360 / 13 * a.name.length * CU.S, 65 * CU.S);

            let titleText = TextUtils.addTextAdvanced(scene, a.name.toUpperCase(), 125, 43 + dy,
                '#eec761', multiple? 17:20, 'normal', 350, 'left', 2, Fonts.BENGALY_NORMAL);
            titleText.setStroke('#000000', 1);

            let effectIcon = SpriteUtils.addImage(this.scene, titleText.getBottomLeft().x! / CU.S + 10, titleText.y / CU.S + 30,
                this.chooseIcon(a.effectType)!, undefined, 0.7);

            let effectType = ArtifactUtils.getEffectTypeText(a.effectType);

            let effectText = TextUtils.addTextAdvanced(scene, effectType!.toUpperCase() + ' +' + SkillUtils.formatValue(a.effectValue), effectIcon.x / CU.S + 20, effectIcon.y / CU.S,
                '#8bb652', 14, 'normal', 350, 'left', 2, Fonts.ARIAL_MT);

            let statIcon = SpriteUtils.addImage(this.scene!, effectText.getTopRight().x! / CU.S + 27, effectText.y / CU.S + 10 ,
                Keys.ATLAS_UI, Keys.UI_STAT_MINE_CIRCLE)
                .setOrigin(0.8);
            statIcon.setInteractive().on('pointerup', function (pointer: any) {
                OnceCaller.doOnce('artManual', () => {
                    manualCallback(a.artifactId);
                });
            });

            let levelText = TextUtils.addTextAdvanced(scene, (Tr('uroven') + ' ' + a.level + ' ' + Tr('of') + ' ' + a.maxLevel).toUpperCase(), titleText.x / CU.S, effectText.y / CU.S + 30,
                '#dddddd', 11, 'normal', 350, 'left', 2, Fonts.ARIAL_MT);

            let maxAttackText = TextUtils.addTextAdvanced(scene, Tr('maks.dot') + effectType!.toUpperCase() + ': ' + SkillUtils.formatValue(a.effectMaxValue), titleText.x / CU.S, levelText.y / CU.S + 25,
                '#626262', 11, 'normal', 350, 'left', 2, Fonts.ARIAL_MT);

            gameObjects.push(imageLiner,image, titleLight, titleText, effectIcon, effectText, levelText, maxAttackText, statIcon)
            dy += 130;
        });

        let buyButton = SpriteUtils.scale(new SquareButton(this.scene, 520, multiple? 130 : 80, -10, Keys.UI_ART_GREEN_BUTTON,
            undefined, Keys.IC_100_MITHRIL, Tr('kupit'), false), 0.8 / CU.S);

        buyButton.setCallback(() => {
            SoundUtils.play(clickSound);
            callback();
        });

        const separator = SpriteUtils.addSprite(this.scene!, 0, this.height, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_SEPARATOR).setOrigin(0, 1);

        this.add([back, ...gameObjects, separator,
            buyButton]);

        let oldPrice = sa.oldPrice.mithril;
        let price = sa.price.mithril;

        let priceText;
        if (oldPrice === price) {
            priceText = TextUtils.addText(scene,
                TextUtils.formatGems(price), buyButton.x / CU.S, buyButton.y / CU.S + 32, '#ffffff', 18, 'bold', 200);
        } else {
            priceText = TextUtils.addTextAdvanced(scene, TextUtils.formatGems(price), buyButton.x / CU.S, buyButton.y / CU.S + 32,
                '#0cff00', 19, 'normal', 200, 'center', 2, Fonts.MYRIADPRO_BOLD);

            priceText.setShadow(0, 0, '#ffffff');
            priceText.setStroke('#000000', 2);
        }
        this.add(priceText);

        if (oldPrice !== price) {
            let oldPriceText = TextUtils.addTextAdvanced(scene, TextUtils.formatGems(oldPrice), buyButton.x / CU.S, buyButton.y / CU.S + 10,
                '#ffffff', 13, 'normal', 200, 'center', 2, Fonts.MYRIADPRO_REGULAR);
            oldPriceText.setShadow(0, 0, '#ffffff');
            oldPriceText.setStroke('#000000', 2);

            let underline = scene.add.line(0, 0,
                oldPriceText.getLeftCenter().x! + 18 * CU.S, oldPriceText.getLeftCenter().y!,
                oldPriceText.getRightCenter().x! + 14 * CU.S, oldPriceText.getRightCenter().y! + 14 * CU.S, 0xFF0000, 1);

            this.add([oldPriceText, underline]);
        }
    }

    private chooseIcon(effectType: EffectType) {
        switch (effectType) {
            case EffectType.DEFENCE:
                return Keys.UI_SHOP_ARTIFACT_EFFECT_DEFENCE_ICON;
            case EffectType.ATTACK:
                return Keys.UI_SHOP_ARTIFACT_EFFECT_ATTACK_ICON;
            case EffectType.HEALTH:
                return Keys.UI_SHOP_ARTIFACT_EFFECT_HEALTH_ICON;
        }
    }

}
